import {Identify, identify, init} from '@amplitude/analytics-browser';
import {useGetUserQuery} from '@ark/react';
import * as Sentry from '@sentry/react';
import React, {useContext} from 'react';

interface UserProfileContext {
  id?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  roles?: string[];
  mobile?: string | null;
  communityManagerCommunities?: string[];
  teamIds?: string[];
  profileId?: string | null;
  displayPicture?: string | null;
  biography?: string | null;
  residentAppVisibility?: boolean | null;
  displayRole?: string | null;
  loading?: boolean;
}

const defaultUserState: UserProfileContext = {
  id: undefined,
  email: undefined,
  firstName: undefined,
  lastName: undefined,
  roles: ['DE'],
  mobile: undefined,
  teamIds: [],
  loading: true,
};

export const userProfileContext =
  React.createContext<UserProfileContext>(defaultUserState);

interface UserProfileProviderProps {
  children: React.ReactNode;
}

export const useUserProfile = () => {
  const context = useContext(userProfileContext);
  if (!context) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};

export const UserProfileProvider = (props: UserProfileProviderProps) => {
  const [state, setState] =
    React.useState<UserProfileContext>(defaultUserState);
  const {data: userData, loading} = useGetUserQuery({
    fetchPolicy: 'cache-and-network',
  });

  React.useEffect(() => {
    const roles: string[] = [];
    if (userData?.getUser) {
      if (userData.getUser.clientAdmin?.id) {
        roles.push('AD');
      }
      if (userData.getUser.communityManager?.isActive) {
        roles.push('CM');
      }
      if (userData.getUser.manager?.isActive) {
        roles.push('MA');
      }
      if (userData.getUser.concierge?.isActive) {
        roles.push('CO');
      }
      if (roles.length === 0) {
        roles.push('DE');
      }

      setState(state => {
        return {
          ...state,
          id: userData?.getUser?.id,
          email: userData?.getUser?.email,
          firstName: userData?.getUser?.firstName,
          lastName: userData?.getUser?.lastName,
          mobile: userData?.getUser?.mobile,
          roles,
          communityManagerCommunities:
            userData?.getUser?.communityManager?.communities?.edges.map(
              edge => {
                if (!edge?.node) {
                  throw new Error('Unexpected null or undefined node');
                }
                return edge.node.id;
              },
            ) || [],
          teamIds:
            userData?.getUser?.teams?.edges.map(edge => {
              if (!edge?.node) {
                throw new Error('Unexpected null or undefined node');
              }
              return edge.node.id;
            }) || [],
          profileId: userData?.getUser?.profile?.id,
          displayPicture: userData?.getUser?.profile?.displayPicture,
          biography: userData?.getUser?.profile?.biography,
          residentAppVisibility:
            userData?.getUser?.profile?.residentAppVisibility,
          displayRole: userData?.getUser?.profile?.displayRole,
          loading,
        };
      });
    }
    Sentry.setUser({
      email: userData?.getUser?.email,
    });
    const manager = userData?.getUser?.manager;
    if (manager && manager.client?.clientType) {
      const identifyObj = new Identify().set('Permission level', 'Manager');

      const apiKey =
        manager.client?.clientType === 'ENT'
          ? import.meta.env.VITE_AMPLITUDE_API_KEY_ENT
          : import.meta.env.VITE_AMPLITUDE_API_KEY_BTL;

      if (apiKey) {
        init(apiKey, manager.id);
      }

      identify(identifyObj);
    }
  }, [userData]);

  return (
    <userProfileContext.Provider value={state}>
      {props.children}
    </userProfileContext.Provider>
  );
};
